import AgoraRTC from 'agora-rtc-sdk-ng';
import {AGORA_RTC_MODE, AGORA_APP_ID,
  VIDEO_SERVICE_DEFAULT_WIDTH,
  VIDEO_SERVICE_DEFAULT_HEIGHT,
  VIDEO_SERVICE_DEFAULT_FRAMERATE} from '../configs/constants';
import {convertTo32BitInteger} from './';

const TAG = 'VideoServiceNg';

window.AgoraNG = AgoraRTC;

export class TrackStream {
  _setAudioEnabled = (value) => {
    if (this.hasAudio()) {
      if (this.audio.setEnabled) {
        this.audio.setEnabled(value);
      } else {
        this.audio.setVolume(value ? 100 : 0);
      }
    }
  }
  _setVideoEnabled = (value) => {
    if (this.hasVideo()) {
      if (this.video.setEnabled) {
        this.video.setEnabled(value);
      } else if (!value) {this.video.stop();} else {this.video.play(this.videoPlaybackElement);}
    }
  }
  constructor(audio, video, remoteUserId, isLocal = false) {
    this.audio = !audio ? null : audio;
    this.video = !video ? null : video;
    this.localUser = isLocal;
    this.remoteUserId = !remoteUserId ? null : remoteUserId;
    this.videoPlaybackElement = null;
  }
  hasAudio = () => typeof (this.audio) !== 'undefined' && this.audio !== null;
  hasVideo = () => typeof (this.video) !== 'undefined' && this.video !== null;
  isClosed = () => !this.hasAudio() && !this.hasVideo();
  play = (element) => {
    if (this.hasAudio() && !this.localUser) {
      try {
        this.audio.play();
      } catch (e) {
        console.error(`${TAG} LocalStream.play: audio playback failed: ${e}`);
      }
    }
    if (this.hasVideo()) {
      this.videoPlaybackElement = element;
      try {
        this.video.play(element);
      } catch (e) {
        console.error(`${TAG} LocalStream.play: video playback failed: ${e}`);
      }
    }
  }
  stop = () => {
    if (this.hasAudio()) {this.audio.stop();}
    if (this.hasVideo()) {this.video.stop();}
    this.videoPlaybackElement = null;
  }
  close = () => {
    this.stop();
    if (this.hasAudio()) {
      this.audio.close();
      this.audio = null;
    }
    if (this.hasVideo()) {
      this.video.close();
      this.video = null;
    }
  }
  getAudioLevel = () => {
    if (this.hasAudio()) {
      return this.audio.getVolumeLevel();
    }
    return 0;
  }
  getId = () => this.remoteUserId;
  getLocalTracks = () => {
    const tracks = [];
    if (this.hasVideo()) {
      tracks.push(this.video);
    }
    if (this.hasAudio()) {
      tracks.push(this.audio);
    }
    return tracks;
  }
  setVideoProfile = (profile) => {
    if (this.hasVideo()) {
      this.video.setEncoderConfiguration(profile);
    }
  }
  disableAudio = () => this._setAudioEnabled(false);
  enableAudio = () => this._setAudioEnabled(true);
  disableVideo = () => this._setVideoEnabled(false);
  enableVideo = () => this._setVideoEnabled(true);
}

export default class VideoServiceNG {
  static checkSystemRequirements() {
    return AgoraRTC && AgoraRTC.checkSystemRequirements();
  }
  _checkPermissionException(e) {
    this.permissionsGranted = !(e.code === 'PERMISSION_DENIED');
  }
  _mapRemoteUserToStream = (value) => {
    if (!value) {return null;}
    return new TrackStream(value.audioTrack, value.videoTrack, value.uid);
  }
  _resetEventListeners = () => {
    this.remoteStreamAddedListener = () => {};
    this.remoteStreamRemovedListener = () => {};
    this.videoMutedListener = () => {};
    this.videoUnmutedListener = () => {};
  }
  _resetValues = () => {
    this.permissionsGranted = false;
    this.localStream = new TrackStream(null, null, this.getUserId());
    this.mutedList = new Map();
    this.userChannelId = null;
    this.localStreamPublished = false;
  }
  _onClientError = (error) => {
    const key = null;
    console.log('RTC client error:', error.reason);

    if (this.client && error.reason === 'DYNAMIC_KEY_TIMEOUT') {
      this.client.renewChannelKey(key, () => {
        console.log('Renewed channel key successfully');
      }, (err) => {
        console.log('Renewed channel key with error: ', err);
      });
    }
  }
  constructor() {
    const service = this;
    this.client = AgoraRTC.createClient({mode: AGORA_RTC_MODE, codec: 'h264'});
    this._resetValues();
    this._resetEventListeners();
    this.client.on('user-info-updated', (uid, msg) => {
      const mutedItem = service.mutedList.has(uid) ? service.mutedList.get(uid) : null;
      switch (msg) {
        case 'mute-video':
          if (mutedItem) {
            mutedItem.video = true;
          }
          this.videoMutedListener(uid);
          break;
        case 'unmute-video':
          if (mutedItem) {
            mutedItem.video = false;
          }
          this.videoUnmutedListener(uid);
          break;
        case 'mute-audio':
          if (mutedItem) {
            mutedItem.audio = true;
          }
          break;
        case 'unmute-audio':
          if (mutedItem) {
            mutedItem.audio = false;
          }
          break;
        default:
          break;
      }
    });
    this.client.on('user-published', async (remoteUser, mediaType) => {
      console.log(`${TAG} user-published ${mediaType}`);
      try {
        await this.client.subscribe(remoteUser, mediaType);
        const stream = this._mapRemoteUserToStream(remoteUser);
        this.remoteStreamAddedListener(stream, stream.getId());
      } catch (e) {
        console.error(`${TAG} user-published ${e}`);
      }
    });
    this.client.on('user-unpublished', async (remoteUser, mediaType) => {
      console.log(`${TAG} user-unpublished ${mediaType}`);
    });
    this.client.on('user-joined', (remoteUser) => {
      const uid = remoteUser.uid;
      console.log(`${TAG} user-joined ${uid}`);
      this.mutedList.set(uid, {audio: remoteUser.hasAudio, video: remoteUser.hasVideo});
    });
    this.client.on('user-left', (remoteUser, reason) => {
      const uid = remoteUser.uid;
      console.log(`${TAG} user-left ${uid} ${reason}`);
      this.mutedList.delete(uid);
      const stream = new TrackStream(null, null, remoteUser.uid);
      this.remoteStreamRemovedListener(stream, stream.getId());
    });
  }
  isPermissionsGranted = () => this.permissionsGranted;
  getLocalStream = () => this.localStream;
  getRemoteStreams = () => {
    if (!this.client) {return [];}
    return this.client.remoteUsers.map(v => this._mapRemoteUserToStream(v));
  }
  findRemoteStreamById = (id) => {
    if (!this.client) {return null;}
    const user = this.client.remoteUsers.find(v => v.uid === id);
    return this._mapRemoteUserToStream(user);
  }
  getUserId = () => {
    if (this.client) {
      return this.client.uid ? this.client.uid : null;
    }
    return null;
  }
  getDevices = async () => {
    try {
      this.permissionsGranted = true;
      return await AgoraRTC.getDevices(false);
    } catch (e) {
      this._checkPermissionException(e);
      console.error(`${TAG} gotDevices ${e}`);
      return [];
    }
  }
  /**
   * params = {
      streamId?,
      audio: Boolean(audioSource),
      video: Boolean(videoSource),
      cameraId
      microphoneId
      mirror
      screen
   }
    */
  createLocalStream = async (params) => {
    let localAudio = null;
    let localVideo = null;
    try {
      this.localStream.close();
      if (params.audio) {
        localAudio = await AgoraRTC.createMicrophoneAudioTrack(
          {microphoneId: params.microphoneId});
      }
      if (params.video) {
        localVideo = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: {
            mirror: params.mirror,
            width: params.width ? params.width : VIDEO_SERVICE_DEFAULT_WIDTH,
            height: params.height ? params.height : VIDEO_SERVICE_DEFAULT_HEIGHT,
            frameRate: params.frameRate ? params.frameRate : VIDEO_SERVICE_DEFAULT_FRAMERATE,
          },
          cameraId: params.cameraId});
      }
      this.permissionsGranted = true;
    } catch (e) {
      this._checkPermissionException(e);
      console.error(`${TAG} createLocalStream ${e}`);
      if (localAudio !== null) {localAudio.close();}
      if (localVideo !== null) {localVideo.close();}
      localAudio = null;
      localVideo = null;
    }
    this.localStream = new TrackStream(localAudio, localVideo, this.getUserId(), true);
  }
  joinGame = async (
    userId,
    gameId) => {
    try {
      this.userChannelId = await this.client.join(
        AGORA_APP_ID, gameId, null, Math.abs(convertTo32BitInteger(userId)));
      console.log(`${TAG} joinGame ${this.userChannelId}`);
    } catch (e) {
      console.error(`${TAG} joinGame ${e}`);
    }
    return this.userChannelId;
  }
  publishLocalStream = async (isAudience = false) => {
    if (this.localStreamPublished) {
      return false;
    }
    try {
      const role = isAudience ? 'audience' : 'host';
      await this.client.setClientRole(role);
      await this.client.publish(this.localStream.getLocalTracks());
      this.localStreamPublished = true;
      console.log(`${TAG} publishLocalStream published`);
      return true;
    } catch (e) {
      this.localStreamPublished = false;
      console.error(`${TAG} publishLocalStream ${e}`);
      return false;
    }
  }
  unPublishLocalStream = async () => {
    if (!this.localStreamPublished) {
      return false;
    }
    try {
      await this.client.unpublish(this.localStream.getLocalTracks());
      this.localStreamPublished = true;
      return true;
    } catch (e) {
      console.error(`${TAG} unPublishLocalStream ${e}`);
      this.localStreamPublished = false;
      return false;
    }
  }
  stop = async () => {
    try {
      await this.unPublishLocalStream();
      this.localStream.close();
      await this.client.leave();
    } catch (e) {
      console.error(`${TAG} stop ${e}`);
    } finally {
      this._resetValues();
      this._resetEventListeners();
    }
  }
}